import { BufferWriter, Writer } from "protobufjs";

import { Client } from "../../../domain/model/client";
import {
  IClientRepository,
  TCreateClient,
  TUpdateClientRole,
} from "../../../domain/repository/client_repository";

import { IApiClient } from "../client";
import { api, common, entity } from "../rpc/api";
import { getToken } from "./bind_token";

import ClientID = entity.ClientID;
import ClientIDList = entity.ClientIDList;
import ClientList = entity.ClientList;
import CreateClientRequest = api.CreateClientRequest;
import UpdateClientRequest = api.UpdateClientRequest;
import UpdateClientRoleRequest = api.UpdateClientRoleRequest;
import Empty = common.Empty;

import { NotificationClient } from "../../../config/view_const/notification";
import { AppError, ErrorType } from "../../../domain/model/app_error";
import {
  ClientConvertResponse,
  ClientConvertResponseList,
} from "./client_api_convert";

class ClientAPI implements IClientRepository {
  constructor(readonly apiClient: IApiClient) {}

  public getAll(): Promise<Client[]> {
    return new Promise<Client[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: Empty = new Empty();
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ClientService/GetAll",
            token,
            Empty.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: ClientList = ClientList.decode(binary);
          // BE=>FE 型変換
          const resConverted: ClientConvertResponseList =
            ClientConvertResponseList.from(res);
          const items: Client[] = resConverted.items.map(
            (item: ClientConvertResponse): Client => {
              return Client.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getAllByIDs(ids: string[]): Promise<Client[]> {
    return new Promise<Client[]>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: ClientIDList = new ClientIDList();
          req.ids = ids;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ClientService/GetAllByIDs",
            token,
            ClientIDList.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: ClientList = ClientList.decode(binary);
          // BE=>FE 型変換
          const resConverted: ClientConvertResponseList =
            ClientConvertResponseList.from(res);
          const items: Client[] = resConverted.items.map(
            (item: ClientConvertResponse): Client => {
              return Client.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public get(clientID: string): Promise<Client> {
    return new Promise<Client>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: ClientID = new ClientID();
          req.id = clientID;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ClientService/Get",
            token,
            ClientID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.Client = entity.Client.decode(binary);
          // BE=>FE 型変換
          const resConverted: ClientConvertResponse =
            ClientConvertResponse.from(res);
          const item: Client = Client.from(resConverted);
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public create(createClientReq: TCreateClient): Promise<Client> {
    return new Promise<Client>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: CreateClientRequest = new CreateClientRequest();
          req.name = createClientReq.name;
          req.nameKana = createClientReq.nameKana;
          req.phoneNumber = createClientReq.phoneNumber;
          req.position = createClientReq.position;
          req.email = createClientReq.email;
          req.password = createClientReq.password;
          req.role = createClientReq.role;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ClientService/Create",
            token,
            CreateClientRequest.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.Client = entity.Client.decode(binary);
          // BE=>FE 型変換
          const resConverted: ClientConvertResponse =
            ClientConvertResponse.from(res);
          const item: Client = Client.from(resConverted);
          resolve(item);
        })
        .catch((error: Error): void => {
          if (error instanceof AppError) {
            if (error.type === ErrorType.API_REQUEST_403) {
              reject(
                new AppError(
                  error.code,
                  ErrorType.FAIL_CLIENT_MEMBER_CREATE,
                  NotificationClient.failureCreateClient
                )
              );
            } else {
              reject(error);
            }
          }
        });
    });
  }

  public update(
    name: string,
    nameKana: string,
    iconURL: string,
    phoneNumber: string,
    department: string,
    position: string
  ): Promise<Client> {
    return new Promise<Client>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: UpdateClientRequest = new UpdateClientRequest();
          req.name = name;
          req.nameKana = nameKana;
          req.iconURL = iconURL;
          req.phoneNumber = phoneNumber;
          req.position = position;
          req.department = department;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ClientService/Update",
            token,
            UpdateClientRequest.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.Client = entity.Client.decode(binary);
          // BE=>FE 型変換
          const resConverted: ClientConvertResponse =
            ClientConvertResponse.from(res);
          const item: Client = Client.from(resConverted);
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public updateRole(updateClientRoleReq: TUpdateClientRole): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: UpdateClientRoleRequest = new UpdateClientRoleRequest();
          req.clientID = updateClientRoleReq.clientID;
          req.role = updateClientRoleReq.role;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ClientService/UpdateRole",
            token,
            UpdateClientRoleRequest.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          resolve();
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public delete(clientID: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      getToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: ClientID = new ClientID();
          req.id = clientID;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.ClientService/Delete",
            token,
            ClientID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          resolve();
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }
}

const createClientAPI = (apiClient: IApiClient): IClientRepository => {
  return new ClientAPI(apiClient);
};

export { createClientAPI };



// WEBPACK FOOTER //
// ./src/infra/api/service/client_api.ts